// @owners { team: patients-team }
import { Body, Button, Column, ExternalLink, InputZip, Row } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
// eslint-disable-next-line @alto/prefer-react-hook-form
import { Field, type FieldProps, Form, Formik } from 'formik';
import React, { useEffect } from 'react';
// eslint-disable-next-line import/no-deprecated
import { fetchServiceAvailability, updateTempSignupUser } from '~shared/actions/auth';
import { PRIVACY_POLICY_URL } from '~shared/constants';
import getURLParam from '~shared/helpers/getURLParam';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useDispatchShared } from '~shared/store';
import { validatePresentWithMessage, validateZipCode } from '~shared/validations';

type Values = {
  zip: string;
};

export type Props = {
  readonly disabled?: boolean;
};

const validateForm = ({ zip }: Values) => {
  const zipErrors = validatePresentWithMessage('Zip Code')(zip) || validateZipCode(zip);

  if (zipErrors) {
    return {
      zip: zipErrors,
    };
  }

  return {};
};

export const ServiceAreaForm = ({ disabled = false }: Props) => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    const origin = getURLParam('origin', window.location.href);
    trackPageView({
      event: EVENTS.ZIP_VERIFICATION_VIEWED,
      params: { flow: 'sign up', origin },
    });
  }, [trackPageView]);

  const handleSubmitZip = (values: Values) => {
    const url = new URL(window.location.href);

    // Preserve the existing query params
    const params = new URLSearchParams(url.search);
    params.set('zip', values.zip);
    const destination = `${url.pathname}?${params.toString()}`;

    const referrerID = params.get('referred_by_id');

    if (referrerID) {
      dispatch(
        updateTempSignupUser({
          referred_by_id: Number(referrerID),
        }),
      );
    }

    // eslint-disable-next-line import/no-deprecated
    dispatch(fetchServiceAvailability(values.zip));
    navigate(destination);
  };

  return (
    <Formik
      initialValues={{
        zip: '',
      }}
      onSubmit={handleSubmitZip}
      validate={validateForm}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleSubmit }) => (
        <Form>
          <Column gap="MD">
            <Body>Let's check if we serve your area. What's your zip code?</Body>
            <Field name="zip">
              {({ field, form, meta }: FieldProps<string>) => (
                <InputZip
                  error={meta.error}
                  label="Zip Code"
                  onBlur={field.onBlur}
                  onChangeText={form.handleChange('zip')}
                  placeholder="Zip code"
                  value={field.value}
                />
              )}
            </Field>
            <Row centerHorizontally>
              <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>
            </Row>
            <Button
              width="full"
              type="primary"
              disabled={disabled}
              onPress={handleSubmit}
            >
              Submit
            </Button>
          </Column>
        </Form>
      )}
    </Formik>
  );
};
